import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Eye, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/visitors`;
const HEARTBEAT_MS = 15000;

const getSessionId = () => {
  let sid = sessionStorage.getItem('ewrf_visitor_sid');
  if (!sid) {
    sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('ewrf_visitor_sid', sid);
  }
  return sid;
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return '–';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return n.toLocaleString();
};

/**
 * Displays both live viewers (sessions active in last 30s) and the
 * all-time total unique visitor count.
 */
export const LiveVisitors = ({ variant = 'badge' }) => {
  const [stats, setStats] = useState({ live: null, total: null });
  const sidRef = useRef(getSessionId());

  useEffect(() => {
    let cancelled = false;

    const beat = async () => {
      try {
        const res = await axios.post(`${API}/heartbeat`, { session_id: sidRef.current, path: window.location.pathname });
        if (!cancelled) setStats({ live: res.data?.live ?? 0, total: res.data?.total ?? 0 });
      } catch {
        // ignore
      }
    };

    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const { live, total } = stats;
  if (live === null) return null;

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-4 text-xs font-semibold" data-testid="live-visitors">
        <span className="inline-flex items-center gap-1.5 text-emerald-300">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
          </span>
          <Eye className="w-3.5 h-3.5" />
          <span className="font-black">{formatNumber(live)}</span>
          <span className="text-white/70 hidden sm:inline">live</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-amber-300">
          <Users className="w-3.5 h-3.5" />
          <span className="font-black" data-testid="total-visitors">{formatNumber(total)}</span>
          <span className="text-white/70 hidden sm:inline">total visits</span>
        </span>
      </span>
    );
  }

  // floating bottom-left badge
  return (
    <div
      className="fixed bottom-5 left-5 z-40 bg-white/95 backdrop-blur-md border border-gray-200 rounded-2xl shadow-2xl overflow-hidden text-sm"
      data-testid="live-visitors"
    >
      <div className="flex divide-x divide-gray-200">
        <div className="flex items-center gap-2 px-3.5 py-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <div>
            <div className="font-black text-emerald-700 text-base leading-none">{formatNumber(live)}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-0.5">viewing now</div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3.5 py-2.5">
          <Users className="w-4 h-4 text-[#1565C0]" />
          <div>
            <div className="font-black text-[#1565C0] text-base leading-none" data-testid="total-visitors">
              {formatNumber(total)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-0.5">total visits</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveVisitors;
